import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Form, InputGroup, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const CustomersList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { token } = useContext(AuthContext);
  
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch('/api/admin/customers', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch customers');
        }
        
        const data = await response.json();
        setCustomers(data);
        setFilteredCustomers(data);
      } catch (err) {
        console.error('Error fetching customers:', err);
        toast.error('Failed to load customers');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCustomers();
  }, [token]);
  
  useEffect(() => {
    // Filter customers based on search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = customers.filter(
        customer => 
          customer.CompanyName.toLowerCase().includes(term) ||
          (customer.Email && customer.Email.toLowerCase().includes(term)) ||
          (customer.Address && customer.Address.toLowerCase().includes(term)) ||
          (customer.City && customer.City.toLowerCase().includes(term)) ||
          (customer.State && customer.State.toLowerCase().includes(term)) ||
          (customer.Phone && customer.Phone.toLowerCase().includes(term))
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleCustomerStatusToggle = async (customerId, isActive) => {
    try {
      const response = await fetch(`/api/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          isActive: !isActive,
          // Include required fields to avoid validation errors
          // These will be the same as existing values
          companyName: customers.find(c => c.CustomerId === customerId).CompanyName,
          address: customers.find(c => c.CustomerId === customerId).Address,
          city: customers.find(c => c.CustomerId === customerId).City,
          state: customers.find(c => c.CustomerId === customerId).State,
          zipCode: customers.find(c => c.CustomerId === customerId).ZipCode,
          phone: customers.find(c => c.CustomerId === customerId).Phone,
          email: customers.find(c => c.CustomerId === customerId).Email
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update customer status');
      }
      
      const updatedCustomer = await response.json();
      
      // Update customers state
      setCustomers(customers.map(customer => 
        customer.CustomerId === customerId ? updatedCustomer : customer
      ));
      
      toast.success(`Customer ${updatedCustomer.IsActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Error updating customer status:', err);
      toast.error('Failed to update customer status');
    }
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading customers...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Customers</h2>
        <Button as={Link} to="/admin/customers/create" variant="primary">
          Add New Customer
        </Button>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <Form.Control
              placeholder="Search customers by name, email, address, etc."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {filteredCustomers.length > 0 ? (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => (
                    <tr key={customer.CustomerId}>
                      <td>{customer.CompanyName}</td>
                      <td>{customer.Email || 'N/A'}</td>
                      <td>{customer.Phone || 'N/A'}</td>
                      <td>
                        {customer.City && customer.State
                          ? `${customer.City}, ${customer.State}`
                          : 'N/A'
                        }
                      </td>
                      <td>
                        <Badge bg={customer.IsActive ? 'success' : 'danger'}>
                          {customer.IsActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/admin/customers/${customer.CustomerId}`}
                          variant="primary"
                          size="sm"
                          className="me-2"
                        >
                          View
                        </Button>
                        <Button
                          variant={customer.IsActive ? 'warning' : 'success'}
                          size="sm"
                          className="me-2"
                          onClick={() => handleCustomerStatusToggle(customer.CustomerId, customer.IsActive)}
                        >
                          {customer.IsActive ? 'Deactivate' : 'Activate'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-center">
              {customers.length > 0 
                ? 'No customers match your search criteria.' 
                : 'No customers found. Add your first customer.'}
            </p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CustomersList;
